import { z } from 'zod'

/**
 * Environment, validated at startup so a missing variable crashes at boot
 * rather than surfacing as a broken screen three routes in (§6.12).
 */
const Env = z.object({
  VITE_API_URL: z.string().min(1).default('/api'),
  VITE_USE_MOCKS: z
    .string()
    .optional()
    .transform((value) => value === 'true'),
  /**
   * The public origin, for absolute URLs in metadata.
   *
   * `window.location.origin` is right at runtime and WRONG at prerender time:
   * the snapshot is taken against a throwaway local server, so canonical,
   * `og:url` and every `hreflang` were being baked as `http://localhost:4178`
   * — correct-looking in `dist`, and nonsense in every share.
   *
   * Optional, because a normal dev run has no meaningful public origin and
   * falls back to the live one. The prerender step refuses to run without it.
   */
  VITE_SITE_URL: z.string().url().optional(),
  /**
   * The public website (coeurentete.ca), where the logo on the signed-out pages
   * leads — distinct from VITE_SITE_URL, which is this app's own origin.
   */
  VITE_WEBSITE_URL: z.string().url().default('https://www.coeurentete.ca'),
  /**
   * Whether the composer offers the Video control (10 Oct 2026).
   *
   * **Default on**, so the capability is the normal state of the app and a mode
   * has to ask to be without it: `VITE_COMMUNITY_VIDEO=false`. Hence
   * `!== 'false'` and not `=== 'true'` — an absent variable means on here,
   * which is the opposite of how `VITE_USE_MOCKS` above reads, and deliberately
   * so.
   *
   * It exists at all because the server side is behind `COMMUNITY_VIDEO_POSTS`,
   * which production cannot have on until ffmpeg is installed on the host — it
   * is not in the Amazon Linux 2023 repositories. With the server flag off a
   * video upload is refused by name rather than crashing, so this was never a
   * safety net; it is so a member is not offered a button that cannot work. Off
   * turns the control back into the marked shell it ships as.
   */
  VITE_COMMUNITY_VIDEO: z
    .string()
    .optional()
    .transform((value) => value !== 'false'),
})

const parsed = Env.safeParse(import.meta.env)

if (!parsed.success) {
  throw new Error(
    `Invalid environment:\n${parsed.error.issues.map((i) => `  ${i.path.join('.')}: ${i.message}`).join('\n')}`,
  )
}

export const env = parsed.data
